import { useState, useMemo, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, useMediaQuery } from '@mui/material';
import { Restore } from '@mui/icons-material';
import { Page, PageTitleWithBack, Table, Pagination } from '../../../components';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { ResetSpecModal } from './ResetSpecModal';
import { getBuilds, getBuildStatuses, getPeople } from '../../../fetch';
import { IBuild, IBuildStatus, IPeople } from '../../../models';
import { ManageSpecFilters } from './ManageSpecFilters';
import { MobileManageSpec } from './MobileManageSpec';

export const ResetSpecs = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [builds, setBuilds] = useState<IBuild[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('NeighborhoodName');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | null>('');
  const [selectedLotId, setSelectedLotId] = useState<string | null>('');
  const [isLoadingStatuses, setLoadingStatuses] = useState<boolean>(false);
  const [isLoadingAgents, setLoadingAgents] = useState<boolean>(false);
  const [agents, setAgents] = useState<IPeople[] | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [statuses, setStatuses] = useState<IBuildStatus[] | null>(null);
  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    lotNumber?: number | string;
    status?:
      | 'New'
      | 'BuildApproved'
      | 'ContractAaccepted'
      | 'TenDayComplete'
      | 'Closing'
      | 'Complete'
      | string;
    agentId?: string;
  } | null>({});

  const [sortDirection, setSortDirection] = useState<{
    NeighborhoodName?: 'Asc' | 'Desc';
    LotNumber?: 'Asc' | 'Desc';
    State?: 'Asc' | 'Desc';
    City?: 'Asc' | 'Desc';
  }>({
    NeighborhoodName: 'Asc'
  });
  const [currentBuild, setBuild] = useState<IBuild | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchAgents = async () => {
    setLoadingAgents(true);
    try {
      const res = await getPeople({
        perPage: '2147483647', // get all records
        personType: 'InternalAgent'
      });
      setAgents(res.records);
    } catch (error) {
      console.error(error);
    }
    setLoadingAgents(false);
  };

  const fetchOrderStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const res = await getBuildStatuses();
      setStatuses(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingStatuses(false);
  };

  const fetchBuilds = async () => {
    setLoading(true);
    try {
      const res = await getBuilds({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        isSpec: true,
        ...filters
      });
      setBuilds(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(`Error loading builds, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderStatuses();
    fetchAgents();
  }, []);

  useEffect(() => {
    fetchBuilds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, perPage, sortDirection, selectedSort]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleReset = async (build: IBuild) => {
    setShowResetModal(true);
    setBuild(build);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        isServerSorted: selectedSort === 'NeighborhoodName',
        isServerSortedDesc: sortDirection.NeighborhoodName === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodName')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Agents',
        accessor: 'agents',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuild } };
        }) => {
          return (
            <>
              {original.agents &&
                original.agents.length > 0 &&
                original.agents.map((agent, index) => {
                  return (
                    <span key={`${index}`}>
                      {agent.firstName} {agent.lastName}
                      {index !== original.agents.length - 1 ? ', ' : ''}
                    </span>
                  );
                })}
            </>
          );
        }
      },
      {
        Header: 'Buyer',
        accessor: 'buyer',
        sort: false
      },
      {
        Header: 'Phase',
        accessor: 'status',
        sort: false
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 300,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuild } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                color="primary"
                startIcon={<Restore />}
                className={classes.resetButton}
                onClick={() => handleReset(original)}
              >
                Reset
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builds]);

  return (
    <>
      <Page title="Reset Specs">
        <PageTitleWithBack backText="Back to Admin home" backUrl="/admin" title="Reset Specs" />
        <ManageSpecFilters
          isLoadingAgents={isLoadingAgents}
          setSelectedAgent={setSelectedAgent}
          selectedAgent={selectedAgent}
          agents={agents}
          isLoading={isLoading}
          selectedStatus={selectedStatus}
          selectedNeighborhoodId={selectedNeighborhoodId}
          isLoadingStatuses={isLoadingStatuses}
          statuses={statuses}
          selectedLotId={selectedLotId}
          setSelectedNeighborhoodId={setSelectedNeighborhoodId}
          setSelectedLotId={setSelectedLotId}
          setSelectedStatus={setSelectedStatus}
          handleSearch={(clearSearch?: boolean) => {
            setPage(0);
            setFilters({
              neighborhoodId: selectedNeighborhoodId,
              status: selectedStatus as string
            });
          }}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                neighborhoodId: '',
                lotNumber: '',
                status: '',
                agentId: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                neighborhoodId: selectedNeighborhoodId || undefined,
                lotNumber: selectedLotId || undefined,
                status: selectedStatus || undefined,
                agentId: selectedAgent || undefined
              });
            }
          }}
        />
        <Box marginTop="1rem">
          <Table
            columns={columns}
            data={builds}
            isLoading={isLoading}
            hidePagination
            ResponsiveComponent={isMobile ? MobileManageSpec : undefined}
            mobileProps={{
              handleReset: (val: IBuild) => handleReset(val)
            }}
          />
          {!isLoading && (
            <Pagination
              page={page}
              count={recordCount}
              rowsPerPage={perPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </Box>
      </Page>

      <ResetSpecModal
        open={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setBuild(null);
        }}
        currentSpec={currentBuild}
        fetchBuilds={fetchBuilds}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    marginTop: theme.spacing(0.5),
    position: 'relative'
  },
  actionButtons: {
    textAlign: 'right'
  },
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  dataContainer: {
    height: 500,
    overflowX: 'scroll'
  },
  viewModal: {
    marginTop: theme.spacing(1)
  },
  resetButton: {
    textAlign: 'right',
    fontSize: 13
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
